import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useParams } from 'react-router-dom';
import api from '../utils/api';
import ScreenshotUploader from '../components/ScreenshotUploader';
import './Events.css';

const FALLBACK_IMG = '/assets/hero/hero-bg.webp';

function fmtDateTime(value) {
  if (!value) return '';
  const d = new Date(value);
  return d.toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}
function fmtDateRange(start, end) {
  if (!start) return '';
  const s = new Date(start);
  const e = end ? new Date(end) : s;
  const sameDay = s.toDateString() === e.toDateString();
  if (sameDay) {
    const date = s.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
    const startTime = s.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' });
    // No distinct end → show a single time instead of "7:30 am – 7:30 am".
    if (s.getTime() === e.getTime()) {
      return `${date} · ${startTime}`;
    }
    const endTime = e.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' });
    return `${date} · ${startTime} – ${endTime}`;
  }
  return `${fmtDateTime(start)} – ${fmtDateTime(end)}`;
}

function RegistrationForm({ event, onSuccess }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      setError('Name, email and phone are required.');
      return;
    }
    setSubmitting(true);
    try {
      const result = await api.registerForEvent({ event_id: event.id, ...form });
      onSuccess(result);
    } catch (err) {
      setError(err.message || 'Could not register. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <form className="event-form" onSubmit={handleSubmit}>
      {error && <div className="event-form__error">{error}</div>}
      <div className="field">
        <label htmlFor="reg-name">Your name</label>
        <input id="reg-name" name="name" value={form.name} onChange={handleChange} required maxLength={120} />
      </div>
      <div className="field">
        <label htmlFor="reg-email">Email</label>
        <input id="reg-email" name="email" type="email" value={form.email} onChange={handleChange} required maxLength={190} />
      </div>
      <div className="field">
        <label htmlFor="reg-phone">Phone</label>
        <input id="reg-phone" name="phone" type="tel" value={form.phone} onChange={handleChange} required maxLength={40} />
      </div>
      <div className="field">
        <label htmlFor="reg-message">Anything we should know? (optional)</label>
        <textarea id="reg-message" name="message" value={form.message} onChange={handleChange} maxLength={4000} />
      </div>
      <button type="submit" className="btn-primary event-sidebar__cta" disabled={submitting}>
        {submitting ? 'Registering…' : 'Register now'}
      </button>
    </form>
  );
}

function EnquiryForm({ event, onSuccess }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      setError('Name, email and phone are required.');
      return;
    }
    setSubmitting(true);
    try {
      const result = await api.submitEventEnquiry({ event_id: event.id, ...form });
      onSuccess(result);
    } catch (err) {
      setError(err.message || 'Could not send your enquiry. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <form className="event-form" onSubmit={handleSubmit}>
      {error && <div className="event-form__error">{error}</div>}
      <div className="field">
        <label htmlFor="enq-name">Your name</label>
        <input id="enq-name" name="name" value={form.name} onChange={handleChange} required maxLength={120} />
      </div>
      <div className="field">
        <label htmlFor="enq-email">Email</label>
        <input id="enq-email" name="email" type="email" value={form.email} onChange={handleChange} required maxLength={190} />
      </div>
      <div className="field">
        <label htmlFor="enq-phone">Phone</label>
        <input id="enq-phone" name="phone" type="tel" value={form.phone} onChange={handleChange} required maxLength={40} />
      </div>
      <div className="field">
        <label htmlFor="enq-message">What would you like to know? (optional)</label>
        <textarea id="enq-message" name="message" value={form.message} onChange={handleChange} maxLength={4000}
                  placeholder="Pricing, dates, group bookings, anything else…" />
      </div>
      <button type="submit" className="btn-primary event-sidebar__cta" disabled={submitting}>
        {submitting ? 'Sending…' : 'Send enquiry'}
      </button>
    </form>
  );
}

function PaymentScreenshotUpload({ registrationId, uploadToken, onUploaded }) {
  return (
    <ScreenshotUploader
      noun="registration"
      upload={(file) => api.uploadEventPaymentProof({ registration_id: registrationId, upload_token: uploadToken, file })}
      onUploaded={onUploaded}
    />
  );
}

function PaymentSuccess({ result, onProofUploaded }) {
  const { event } = result;
  const { payment_bank_details, payment_instructions, payment_qr_image } = event || {};
  const hasFree = !event || Number(event.cost_inr) === 0;

  // Free events have nothing to pay → a plain confirmation is fine.
  if (hasFree) {
    return (
      <div className="pay-notice">
        <h3 className="pay-notice__title">You're registered.</h3>
        <p className="pay-notice__msg">{result.message}</p>
      </div>
    );
  }

  // Paid events: lead with payment so it never reads as "done". The
  // pending-approval notice appears only AFTER the screenshot is uploaded.
  return (
    <>
      {/* Pay-to details (only when the event actually provides text) */}
      {(payment_instructions || payment_bank_details) && (
        <div className="pay-how">
          {payment_instructions && <p className="pay-how__instructions">{payment_instructions}</p>}
          {payment_bank_details && <pre className="pay-how__bank">{payment_bank_details}</pre>}
        </div>
      )}

      {/* Payment QR picture */}
      {payment_qr_image && (
        <div className="pay-qr">
          <img src={payment_qr_image} alt="Payment QR" className="pay-qr__img" />
        </div>
      )}

      {/* Screenshot upload (required) */}
      {result.registration_id && result.upload_token && (
        <PaymentScreenshotUpload
          registrationId={result.registration_id}
          uploadToken={result.upload_token}
          onUploaded={onProofUploaded}
        />
      )}
    </>
  );
}

function RegistrationModal({ event, isEnquire, existingRegistration, proofUploaded, onProofUploaded, onClose, onRegister }) {
  // Start at step 2 if we're reopening after a paid registration — the user
  // already filled the form and just wants to revisit the payment details.
  const initialStep = existingRegistration && !isEnquire ? 2 : 1;
  const [step, setStep] = useState(initialStep);

  // Paid events require a payment screenshot before the registration can be
  // marked complete. Free events have nothing to pay → no proof. Until the
  // screenshot is uploaded the modal cannot be dismissed (no ✕, overlay or Esc).
  const needsProof = !isEnquire && existingRegistration
    && Number(existingRegistration.event?.cost_inr) > 0;
  const canFinish = !needsProof || proofUploaded;

  const handleGuardedClose = () => { if (canFinish) onClose(); };

  const handleSuccess = (result) => {
    onRegister(result);
    if (!isEnquire) setStep(2);
  };

  // Esc-to-close (only when allowed) + lock background scroll while open.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && canFinish) onClose(); };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, canFinish]);

  return createPortal(
    <div className="event-modal" onClick={handleGuardedClose} role="dialog" aria-modal="true" aria-labelledby="event-modal-title">
      <div className="event-modal__panel" onClick={(e) => e.stopPropagation()}>
        {canFinish && (
          <button type="button" className="event-modal__close" onClick={handleGuardedClose} aria-label="Close">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}

        <div className="event-modal__header">
          <p className="event-modal__eyebrow">{isEnquire ? 'Enquire about' : 'Register for'}</p>
          <h2 id="event-modal-title" className="event-modal__title">{event.title}</h2>
        </div>

        {!isEnquire && (
          <ol className="event-modal__steps" aria-label="Registration progress">
            <li className={`event-modal__step ${step >= 1 ? 'is-active' : ''} ${step > 1 ? 'is-done' : ''}`}>
              <span className="event-modal__step-num">1</span> Your details
            </li>
            <li className="event-modal__step-sep" aria-hidden="true" />
            <li className={`event-modal__step ${step >= 2 ? 'is-active' : ''}`}>
              <span className="event-modal__step-num">2</span> Payment
            </li>
          </ol>
        )}

        <div className="event-modal__body">
          {isEnquire ? (
            existingRegistration ? (
              <div className="event-success">
                <h3 className="event-success__title">Enquiry received.</h3>
                <p className="event-success__msg">{existingRegistration.message}</p>
                <button type="button" className="btn-primary event-sidebar__cta" onClick={onClose}>Done</button>
              </div>
            ) : (
              <EnquiryForm event={event} onSuccess={handleSuccess} />
            )
          ) : step === 1 ? (
            <RegistrationForm event={event} onSuccess={handleSuccess} />
          ) : (
            <PaymentSuccess result={existingRegistration} onProofUploaded={onProofUploaded} />
          )}
        </div>

        {/* Pinned footer (outside the scroll area) for the payment step. */}
        {!isEnquire && step === 2 && (
          <div className="modal-foot">
            <button
              type="button"
              className="btn-primary event-sidebar__cta"
              style={{ opacity: canFinish ? 1 : 0.5 }}
              onClick={onClose}
              disabled={!canFinish}
            >
              {canFinish ? 'Done' : 'Upload screenshot to finish'}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

function EventDetail() {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [registration, setRegistration] = useState(null);
  const [proofUploaded, setProofUploaded] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.getEventBySlug(slug)
      .then((data) => {
        if (!active) return;
        setEvent(data);
        document.title = `${data.title} · Mind Spa`;
      })
      .catch(() => { if (active) setNotFound(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [slug]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setLightbox(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (loading) {
    return <div className="container" style={{ padding: '120px 0', textAlign: 'center' }}>Loading event…</div>;
  }
  if (notFound || !event) {
    return (
      <div className="container" style={{ padding: '120px 0', textAlign: 'center' }}>
        <h2>Event not found</h2>
        <p style={{ marginTop: 12 }}>
          <Link to="/events" style={{ color: 'var(--accent)' }}>← Back to all events</Link>
        </p>
      </div>
    );
  }

  const isEnquire = event.pricing_mode === 'enquire';
  const price = Number(event.cost_inr) || 0;
  const seatsLeft = event.capacity != null ? Math.max(0, event.capacity - (event.seats_taken || 0)) : null;
  const regOpen = event.registration_open && (seatsLeft === null || seatsLeft > 0);
  const eventType = event.event_type || 'offline';
  const eventTypeLabel = eventType === 'online' ? 'Online' : eventType === 'hybrid' ? 'Hybrid' : 'In-person';
  const priceLabel = isEnquire ? 'Enquire for pricing' : (price > 0 ? `₹${price.toLocaleString('en-IN')}` : 'Free');

  return (
    <div className="event-detail">
      {/* Hero */}
      <section className="event-hero">
        <div className="event-hero__bg">
          <img src={event.image || FALLBACK_IMG} alt="" />
        </div>
        <div className="event-hero__overlay" />
        <div className="container event-hero__inner">
          <div className="event-hero__crumbs">
            <Link to="/">Home</Link> &nbsp;/&nbsp; <Link to="/events">Events</Link> &nbsp;/&nbsp; <span>{event.title}</span>
          </div>
          <h1 className="event-hero__title">{event.title}</h1>
          {event.subtitle && <p className="event-hero__subtitle">{event.subtitle}</p>}
          <div className="event-hero__meta">
            <span>📅 {fmtDateRange(event.start_date, event.end_date)}</span>
            {event.venue && <span>📍 {event.venue}</span>}
            <span>{eventType === 'online' ? '💻' : eventType === 'hybrid' ? '🔀' : '🏛️'} {eventTypeLabel}</span>
            <span>💳 {priceLabel}</span>
          </div>
        </div>
      </section>

      <div className="container">
        <div className="event-detail__grid">
          {/* Main column */}
          <div>
            <div className="event-card-panel">
              <h2 className="event-card-panel__title">About this event</h2>
              <div className="event-card-panel__body" dangerouslySetInnerHTML={{ __html: event.description }} />
            </div>

            {event.timetable && event.timetable.trim() !== '' && (
              <div className="event-card-panel">
                <h2 className="event-card-panel__title">Timetable</h2>
                <div className="event-card-panel__body" dangerouslySetInnerHTML={{ __html: event.timetable }} />
              </div>
            )}

            {event.gallery && event.gallery.length > 0 && (
              <div className="event-card-panel">
                <h2 className="event-card-panel__title">Gallery</h2>
                <div className="event-gallery-grid">
                  {event.gallery.map((item) => {
                    const url = item.url || item.image;
                    const isVideo = item.type === 'video';
                    return (
                      <button
                        key={item.id}
                        type="button"
                        className="event-gallery-tile"
                        onClick={() => setLightbox(item)}
                        aria-label={isVideo ? 'Play video' : 'Enlarge image'}
                        style={{ border: 0, padding: 0, position: 'relative' }}
                      >
                        {isVideo ? (
                          <>
                            <video
                              src={url}
                              muted
                              playsInline
                              preload="metadata"
                              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                            />
                            <span
                              aria-hidden="true"
                              style={{
                                position: 'absolute', inset: 0,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                background: 'rgba(0,0,0,0.25)',
                              }}
                            >
                              <svg width="42" height="42" viewBox="0 0 24 24" fill="white" stroke="white" strokeWidth="0.5">
                                <polygon points="6 4 20 12 6 20 6 4" />
                              </svg>
                            </span>
                          </>
                        ) : (
                          <img src={url} alt={item.caption || ''} loading="lazy" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <aside className="event-sidebar">
            <div className="event-card-panel">
              <div className="event-sidebar__price">
                {isEnquire ? 'Enquire' : (price > 0 ? `₹${price.toLocaleString('en-IN')}` : 'Free')}
              </div>
              <p className="event-sidebar__price-note">
                {isEnquire ? 'Pricing on request' : (price > 0 ? 'per attendee' : 'No payment required')}
              </p>

              <dl style={{ margin: 0 }}>
                <div className="event-sidebar__row">
                  <dt>Starts</dt><dd>{fmtDateTime(event.start_date)}</dd>
                </div>
                {event.end_date && event.end_date !== event.start_date && (
                  <div className="event-sidebar__row">
                    <dt>Ends</dt><dd>{fmtDateTime(event.end_date)}</dd>
                  </div>
                )}
                <div className="event-sidebar__row">
                  <dt>Format</dt><dd>{eventTypeLabel}</dd>
                </div>
                {event.venue && (
                  <div className="event-sidebar__row">
                    <dt>Venue</dt><dd>{event.venue}</dd>
                  </div>
                )}
                {!isEnquire && seatsLeft !== null && (
                  <div className="event-sidebar__row">
                    <dt>Seats left</dt><dd>{seatsLeft} of {event.capacity}</dd>
                  </div>
                )}
              </dl>

              {(isEnquire || regOpen) ? (
                event.registration_url ? (
                  <a
                    href={event.registration_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary event-sidebar__cta"
                    style={{ marginTop: 18 }}
                  >
                    Register now&nbsp;
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M7 17L17 7M9 7h8v8" />
                    </svg>
                  </a>
                ) : registration ? (
                  <div style={{ marginTop: 18 }}>
                    <div className="event-sidebar__registered">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      {isEnquire
                        ? 'Enquiry sent — we\'ll be in touch.'
                        : price > 0
                          ? 'Registration pending approval.'
                          : "You're registered."}
                    </div>
                    {!isEnquire && (
                      <button
                        type="button"
                        className="btn-outline event-sidebar__cta"
                        onClick={() => setModalOpen(true)}
                      >
                        View payment details
                      </button>
                    )}
                  </div>
                ) : (
                  <button
                    type="button"
                    className="btn-primary event-sidebar__cta"
                    onClick={() => setModalOpen(true)}
                    style={{ marginTop: 18 }}
                  >
                    {isEnquire ? 'Enquire now' : 'Register now'}
                  </button>
                )
              ) : (
                <div className="event-sidebar__closed">
                  {seatsLeft === 0
                    ? 'This event is full.'
                    : 'Registration is closed for this event.'}
                  <div style={{ marginTop: 10 }}>
                    <Link to="/contact" style={{ color: 'var(--accent)' }}>Contact us to be added to the waitlist →</Link>
                  </div>
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>

      {modalOpen && (
        <RegistrationModal
          event={event}
          isEnquire={isEnquire}
          existingRegistration={registration}
          proofUploaded={proofUploaded}
          onProofUploaded={() => setProofUploaded(true)}
          onClose={() => setModalOpen(false)}
          onRegister={setRegistration}
        />
      )}

      {lightbox && (
        <div className="event-lightbox" onClick={() => setLightbox(null)} role="dialog">
          <button type="button" className="event-lightbox__close" onClick={() => setLightbox(null)} aria-label="Close">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
          {lightbox.type === 'video' ? (
            <video
              src={lightbox.url || lightbox.image}
              controls
              autoPlay
              playsInline
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: '92vw', maxHeight: '92vh', borderRadius: 6, background: '#000' }}
            />
          ) : (
            <img src={lightbox.url || lightbox.image} alt="" onClick={(e) => e.stopPropagation()} />
          )}
        </div>
      )}
    </div>
  );
}

export default EventDetail;
